<template>
    <!-- Main container for centering the form -->
    <div class="flex flex-col items-center justify-center">
        <!-- UAuthForm component for user sign up -->
        <!-- Zod validation schema -->
        <!-- Form fields definition -->
        <!-- Reactive form state -->
        <!-- Icon for the form -->
        <!-- Loading state for submit button -->
        <!-- Custom field properties for styling -->
        <!-- UI customization for leading icon -->
        <!-- Submit button configuration -->
        <!-- Submit event handler -->
        <UAuthForm
            :schema="schema"
            :fields="fields"
            icon="i-lucide-user-circle"
            title="Create Account"
            description="Provide your information below"
            :loading="inProgress"
            :disabled="inProgress"
            :ui="{
                form: 'space-y-5 ',
                header: 'mb-6 mt-6 ',
                leading: 'mb-0 pb-2',
                footer: 'mt-5',
                description: 'text-muted font-normal not-italic',
                leadingIcon: 'size-6.5 text-(--ui-text-primary-highlighted)',
                title: 'text-(--ui-text-primary-highlighted)',
                password: 'mb-1',
            }"
            @submit="signUp"
        >
            <template #validation>
                <CardBlock v-if="authErrorConfig" :config="authErrorConfig"></CardBlock>
            </template>
            <template #submit>
                <UCheckbox
                    v-model="subscribe"
                    class="mb-5 underline decoration-solid decoration-auto decoration-skip-ink-auto underline-offset-auto"
                    label="Subscribe to our newsletter"
                />

                <div class="space-y-4">
                    <!-- Submit Button -->
                    <UButton
                        type="submit"
                        block
                        :loading="inProgress"
                        :disabled="inProgress"
                        class="cursor-pointer h-15 bg-(--color-action-800) hover:bg-(--color-action-900) active:bg-(--color-action-700) disabled:bg-(--color-neutral-800) rounded font-normal"
                    >
                        Create Account
                    </UButton>
                </div>
            </template>
            <!-- Footer slot for additional actions -->
            <template #footer>
                Already have an account ?
                <SharedLinkButton
                    :disabled="inProgress"
                    class="underline decoration-solid decoration-auto decoration-skip-ink-auto underline-offset-auto underline-from-font text-var(--ui-text-primary-subdued) cursor-pointer font-medium"
                    @click.prevent="openLogin"
                >
                    Sign In
                </SharedLinkButton>
            </template>
        </UAuthForm>
    </div>
</template>

<script setup lang="ts">
// Imports
import * as z from 'zod' // Zod for schema validation
import CardBlock from '~~/layers/base/components/shared/CardBlock.vue'
import { getAuthErrorConfig } from '~~/layers/base/data/auth'
import type { SignUp, AuthFormField } from '~~/layers/base/types/auth' // Types for form
import { CHANGE_PASSWORD_REQUIREMENTS_MESSAGE } from '~~/layers/base/utils/passwordPolicy'

// Pinia store instance for authentication
const { signUpAndAssignDesignProject } = useAuthActions()
const customerAuthStore = useCustomerAuthAccsStore()
// Emits for communicating with parent components
const emit = defineEmits(['open-login', 'sign-up-success', 'sign-up-failed'])

const { inProgress, errorMessage } = storeToRefs(customerAuthStore)

const subscribe = ref(true)

// // Reactive state for form data
// const one = reactive<SignUp>({
//     firstName: '', // User's first name
//     lastName: '', // User's last name
//     email: '', // User's email
//     password: '', // User's password
// })

// Form fields definition
// TODO: update these fields to use existing components from the `personalize` page
const commonInputClass =
    'placeholder:opacity-100 h-15 placeholder:italic placeholder:text-(--ui-text-primary-dimmed) text-(--ui-text) ring-2 ring-(--ui-border-primary-elevated)'
const fields: AuthFormField[] = [
    {
        name: 'firstName',
        label: 'First Name',
        type: 'text',
        required: true,
        placeholder: 'Enter your first name',
        ui: {
            base: commonInputClass,
        },
    }, // First name field
    {
        name: 'lastName',
        label: 'Last Name',
        type: 'text',
        required: true,
        placeholder: 'Enter your last name',
        ui: {
            base: commonInputClass,
        },
    }, // Last name field
    {
        name: 'email',
        label: 'Email',
        type: 'text',
        required: true,
        placeholder: 'Enter your email',
        ui: {
            base: commonInputClass,
        },
    },
    {
        name: 'password',
        label: 'Password',
        type: 'password',
        required: true,
        placeholder: 'Enter your password',
        class: 'password-field',
    },
    {
        name: 'confirmPassword',
        label: 'Confirm Password',
        type: 'password',
        placeholder: 'Enter your password',
        required: true,
        class: 'password-field',
    },
]

// Zod schema for form validation
const schema = z
    .object({
        firstName: z.string({ message: 'First name is required' }).min(1, 'First name is required'), // First name validation
        lastName: z.string({ message: 'Last name is required' }).min(1, 'Last name is required'), // Last name validation
        email: z
            .string({ message: 'Email is required' })
            .min(1, 'Email is required.')
            .email('Invalid email'), // Email validation
        password: z
            .string({ message: 'Password is required' })
            .min(1, 'Password is required')
            .regex(/^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9])(?!.*\s).{8,}$/, {
                message: CHANGE_PASSWORD_REQUIREMENTS_MESSAGE,
            }), // Password validation
        confirmPassword: z
            .string({ message: 'Confirm Password is required' })
            .min(1, 'Please confirm your password')
            .regex(/^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9])(?!.*\s).{8,}$/, {
                message: CHANGE_PASSWORD_REQUIREMENTS_MESSAGE,
            }),
    })
    .refine((data) => data.password === data.confirmPassword, {
        path: ['confirmPassword'],
        message: 'Passwords does not match',
    })

onBeforeUnmount(() => {
    errorMessage.value = null
})
// Sign up handler function
const signUp = async (payload: { data: SignUp }): Promise<void> => {
    try {
        await signUpAndAssignDesignProject({ ...payload.data, subscribe: subscribe.value }) // Call store action
        emit('sign-up-success') // Emit success event
    } catch (errorObject: unknown) {
        emit('sign-up-failed', errorObject) // Emit failure event
    }
}

// Open sign in dialog handler
const openLogin = (): void => {
    emit('open-login', 'login') // Emit open sign in event
}

/**
 * CardBlock config for authentication errors
 */
const authErrorConfig = computed(() => {
    if (!errorMessage.value) return undefined
    return getAuthErrorConfig(errorMessage.value)
})
</script>

<style scoped>
:deep(input[type='password']::placeholder) {
    color: var(--ui-text-primary-dimmed) !important;
}
:deep(label) {
    color: var(--ui-text-primary) !important;
    font-size: 0.875rem !important;
    font-style: normal !important;
    margin-bottom: 4px;
}
:deep(.password-field button) {
    display: none !important;
    visibility: hidden !important;
    pointer-events: none !important;
    width: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
}

:deep(.password-field input) {
    height: 3.75rem !important;
    box-shadow: inset 0 0 0 2px var(--ui-border-primary-elevated) !important;
}

:deep(.password-field input:focus-visible:not(.ring-error)) {
    box-shadow: inset 0 0 0 2px var(--ui-border-primary) !important;
}
:deep(.password-field input.ring-error:focus-visible) {
    box-shadow: inset 0 0 0 2px var(--ui-error) !important;
}
</style>
